import type { ActiveFilterChip } from '@/features/header-filters';
import type { SelectOption } from '@/shared/ui/filters';

interface BuildMappingFilterChipsArgs {
  consignmentNumber: string;
  organizationId: string;
  organizationOptions: SelectOption[];
  vehicleCategories: string[];
  vehicleCategoryOptions: SelectOption[];
  onRemove: (key: string) => void;
  onRemoveVehicleCategory: (value: string) => void;
}

const optionLabel = (options: SelectOption[], value: string) =>
  options.find((option) => option.value === value)?.label ?? value;

/**
 * Removable chips for the mapping filters. Vehicle categories are multi-select, so
 * each picked category is its own chip — removing one leaves the rest in place.
 * Pure, so it can be tested without the hook.
 */
export function buildMappingFilterChips({
  consignmentNumber,
  organizationId,
  organizationOptions,
  vehicleCategories,
  vehicleCategoryOptions,
  onRemove,
  onRemoveVehicleCategory
}: BuildMappingFilterChipsArgs): ActiveFilterChip[] {
  const chips: ActiveFilterChip[] = [];

  if (consignmentNumber) {
    chips.push({
      key: 'consignmentNumber',
      label: consignmentNumber,
      onRemove: () => onRemove('consignmentNumber')
    });
  }

  if (organizationId) {
    chips.push({
      key: 'organizationId',
      label: optionLabel(organizationOptions, organizationId),
      onRemove: () => onRemove('organizationId')
    });
  }

  for (const value of vehicleCategories) {
    chips.push({
      key: `vehicleCategory-${value}`,
      label: optionLabel(vehicleCategoryOptions, value),
      onRemove: () => onRemoveVehicleCategory(value)
    });
  }

  return chips;
}
